import React, { useState, useEffect } from 'react';
import { PlusIcon, MinusIcon } from '@heroicons/react/24/outline';
import EtBtnFormBasic from '@/components/common/buttons/EtBtnFormBasic';
import EtContentEditorLight2 from '@/utils/EtContentEditorLight2';
import PulseLoader from 'react-spinners/PulseLoader';
import { useNavigate } from 'react-router';
import { useCreateNewUserMissionGoalMutation } from '@/features/userMissionGoals/userMissionGoalsApiSlice';
import { useGetGoalTypesQuery } from '@/features/goalTypes/goalTypesApiSlice'

const EtCreateUserMissionGoalComp = ({
  userId,
  userOrgId,
  missionId,
  missionModel,
  triggeringUrl,
  onClose,
  refetchOnSuccess,
  missionName="",
  missionUoM="", // 04 Feb 2026
}) => {

    // First Created - 11 Aug 2025
    // Author - Atul Pandey
    // Purpose - To create a new UserMissionGoal
    // Styling inherited from EtCreateUserImprintMissionClientComp
    // Updated - 12 Sep 2025 - added goalUoM
    // Updated - 04 Feb 2026 - added missionUoM and auto populate goal uom if the goaltype is Outcome

    // console.log('missionId', missionId)
    // console.log('missionModel', missionModel )
    // console.log('missionUoM in EtCreateUserMissionGoalComp', missionUoM)
    // console.log('missionModel', missionModel )

  // 1 - Define useNavigate
  const navigate = useNavigate();

  // 1 - State Variables
  const [editorKey, setEditorKey] = useState('goalDesc-');

  // 2 - RTK Queries & Mutations
  const { data: goalTypesData} = useGetGoalTypesQuery()
  const [createNewUserMissionGoal, { isLoading, isSuccess, isError, error }] = useCreateNewUserMissionGoalMutation();

  const extractEntitiesFromAdapter = (dataAdapter) => {
      if (!dataAdapter || !dataAdapter.ids || !dataAdapter.entities) return [];
      return dataAdapter.ids.map((id) => dataAdapter.entities[id])
  };

  const goalTypes = extractEntitiesFromAdapter(goalTypesData)

  // 04 Feb 2026
  const isOutcomeGoalType = (goalTypeId) => {
    const gt = goalTypes.find(g => g._id === goalTypeId)
    return gt?.goalTypeName === 'Outcome'
  }

//   console.log('goalTypes', goalTypes)

  // 3 - Set Form Data
  const [formData, setFormData] = useState({
    goalName: '',
    goalDesc: '',
    goalType: '',
    goalTargetValue: null,
    goalUoM: '', // 12 Sep 2025
    goalBreakdown: [],
    goalNotes: [],
    active: true,
  });

  

  // 4 - 🧠 Submit Handler
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.goalName.trim()) {
      alert('Goal Name is required.');
      return;
    }

    const payload = {
      userId,
      userOrgId,
      missionId,
      missionModel,
      goalName: formData.goalName.trim(),
      goalDesc: formData.goalDesc || '',
      goalType: formData.goalType || null,
      goalTargetValue: formData.goalTargetValue || null,
      goalUoM: formData.goalUoM || "",                  // 12 Sep 2025
      goalBreakdown: formData.goalBreakdown || [],
      goalNotes: formData.goalNotes || [],
      active: formData.active,
    };

    try {
      await createNewUserMissionGoal(payload).unwrap();

      if (refetchOnSuccess) refetchOnSuccess();
      if (onClose) onClose();
      else navigate(triggeringUrl || '/etapp');
    } catch (err) {
      console.error('Error creating goal:', err);
    }
  };

  const handleClear = () => {
    setFormData({
      goalName: '',
      goalDesc: '',
      goalType: '',
      goalTargetValue: null,
      goalUoM: '', // 12 Sep 2025
      goalBreakdown: [],
      goalNotes: [],
      active: true,
    });
  };

  const handleCancel = () => {
    if (onClose) onClose();
    else navigate(triggeringUrl || '/etapp');
  };

  // 04 Feb 2026
  const isOutcomeGoal = isOutcomeGoalType(formData.goalType);

  return (
    <div className="mx-auto w-full m-1 p-1 bg-white rounded shadow-md shadow-black">
      {isLoading && <PulseLoader color={'#ea580c'} />}
      {isError && (
        <p className="text-orange-700 font-semi-bold py-2">
          {error?.data?.message}
        </p>
      )}

      <p className="p-1 m-1 font-bold font-poppins text-xl">
        Create User Mission Goal
      </p>

      <div className='m-1 p-1 px-2 mx-2 flex justify-start items-center bg-orange-100 rounded'>
        <p className="m-1 p-1 block text-gray-600 text-sm font-bold mb-1">
            Mission Name : </p>
        <p className='italic'>{missionName}</p>
      </div>
      
      <form onSubmit={handleSubmit} className="m-1 p-1 text-xs font-poppins">
        {/* Goal Name */}
        <div className="bg-gray-100 rounded p-1 m-1">
          <label className="block text-gray-600 text-sm font-bold mb-1">
            Goal Name *
          </label>
          <input
            type="text"
            name="goalName"
            value={formData.goalName}
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, goalName: e.target.value }))
            }
            placeholder="Enter goal name"
            className="w-full rounded p-2 border border-gray-300 focus:ring-orange-500 focus:border-orange-500"
          />
        </div>

        {/* Goal Desc */}
        <div className="bg-gray-100 rounded p-1 m-1">
          <label className="block text-gray-600 text-sm font-bold mb-1">
            Goal Description
          </label>
          <EtContentEditorLight2
            value={formData.goalDesc}
            handleChange={(content) =>
              setFormData((prev) => ({ ...prev, goalDesc: content }))
            }
            placeholderText="Describe your goal..."
            editorKey={editorKey}
          />
        </div>

        {/* Goal Type - 12 Aug 2025*/}
        {goalTypes?.length > 0 && (
        <div className="bg-gray-100 rounded p-1 m-1">
            <label className="block text-gray-600 text-sm font-bold mb-1">
            Goal Type
            </label>
            <select
            name="goalType"
            value={formData.goalType}
            // 04 Feb 2026

            onChange={(e) => {
              const selectedGoalType = e.target.value;
              const selectedIsOutcome = isOutcomeGoalType(selectedGoalType);

              setFormData((prev) => ({
                ...prev,
                goalType: selectedGoalType,

                // 🔁 04 Feb 2026 – sync / reset goalUoM on transition
                goalUoM: selectedIsOutcome
                  ? missionUoM          // inherit when Outcome
                  : '',                 // wipe when switching away
              }));
            }}
            // onChange={(e) => {
            //   const selectedGoalType = e.target.value;

            //   setFormData((prev) => ({
            //     ...prev,
            //     goalType: selectedGoalType,

            //     // ✅ Auto-populate UoM ONLY for Outcome goals
            //     goalUoM: isOutcomeGoalType(selectedGoalType)
            //       ? missionUoM
            //       : prev.goalUoM,
            //   }));
            // }}
            // onChange={(e) =>
            //     setFormData((prev) => ({
            //     ...prev,
            //     goalType: e.target.value,
            //     }))
            // }
            className="w-full rounded p-2 border border-gray-300 focus:ring-orange-500 focus:border-orange-500"
            >
            <option value="">-- Select Goal Type --</option>
            {goalTypes.map((type) => (
                <option key={type._id} value={type._id}>
                {type.goalTypeName}
                </option>
            ))}
            </select>
        </div>
        )}


        {/* Target Value */}
        <div className="bg-gray-100 rounded p-1 m-1 w-full">
          <label className="block text-gray-600 text-sm font-bold mb-1">
            Goal Target Value
          </label>
          <input
            type="number"
            name="goalTargetValue"
            value={formData.goalTargetValue}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                goalTargetValue: e.target.value,
              }))
            }
            placeholder="Numeric target"
            className="w-full rounded p-2 border border-gray-300 focus:ring-orange-500 focus:border-orange-500"
          />
        </div>

        {/* 12 Sep 2025 - Goal UoM */}
        <div className="bg-gray-100 rounded p-1 m-1">
          <label className="block text-gray-600 text-sm font-bold mb-1">
            Goal UoM * <span className='text-orange-600'>{isOutcomeGoal &&
                `Inherited from Mission (${missionUoM})`
               } </span>
          </label>
          <input
            type="text"
            name="goalUoM"
            value={formData.goalUoM}
            disabled={isOutcomeGoal}   // 🔒 04 Feb 2026 - lock for Outcome goals
            onChange={(e) =>
              setFormData((prev) => ({ ...prev, goalUoM: e.target.value }))
            }
            // 04 Feb 2026
            placeholder={
              isOutcomeGoal
                ? `Inherited from Mission (${missionUoM})`
                : 'Enter goal UoM'
            }
            className={`w-full rounded p-2 border 
              ${isOutcomeGoal 
                ? 'bg-orange-100 text-gray-800 cursor-not-allowed' 
                : 'border-gray-300 focus:ring-orange-500 focus:border-orange-500'
              }`
            }
            // placeholder="Enter goal UoM"
            // className="w-full rounded p-2 border border-gray-300 focus:ring-orange-500 focus:border-orange-500"
          />
        </div>



        {/* Goal Breakdown Section */}
        <div className="border rounded p-2 m-2 bg-gray-50">
          <div className="flex justify-between items-center mb-2">
            <p className="font-bold text-sm">Goal Breakdown</p>
            <button
              type="button"
              className="bg-lime-600 hover:bg-lime-700 text-white text-xs font-bold m-1 p-1 rounded"
              onClick={() =>
                setFormData((prev) => ({
                  ...prev,
                  goalBreakdown: [
                    ...prev.goalBreakdown,
                    {
                      period: '',
                      periodType: '',
                      periodStart: '',
                      periodEnd: '',
                      targetValue: '',
                      actualValue: '',
                      notes: '',
                    },
                  ],
                }))
              }
            >
              <PlusIcon className="h-4 w-4 inline" />
            </button>
          </div>

          {formData.goalBreakdown.map((item, index) => (
            <div key={index} className="p-2 mb-2 border rounded bg-white">
              <div className="flex justify-between items-center mb-2">
                <p className="text-xs font-bold">Breakdown {index + 1}</p>
                <button
                  type="button"
                  className="bg-red-500 hover:bg-red-600 text-white text-xs font-bold p-1 rounded"
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      goalBreakdown: prev.goalBreakdown.filter(
                        (_, idx) => idx !== index
                      ),
                    }))
                  }
                >
                  <MinusIcon className="h-4 w-4 inline" />
                </button>
              </div>

              <div className="grid grid-cols-2 gap-2 text-xs">
                <input
                  type="text"
                  placeholder="Period (e.g., 2025-01)"
                  value={item.period}
                  onChange={(e) =>
                    setFormData((prev) => {
                      const updated = [...prev.goalBreakdown];
                      updated[index].period = e.target.value;
                      return { ...prev, goalBreakdown: updated };
                    })
                  }
                  className="p-1 border rounded"
                />
                <select
                  value={item.periodType}
                  onChange={(e) =>
                    setFormData((prev) => {
                      const updated = [...prev.goalBreakdown];
                      updated[index].periodType = e.target.value;
                      return { ...prev, goalBreakdown: updated };
                    })
                  }
                  className="p-1 border rounded"
                >
                  <option value="">-- Period Type --</option>
                  <option value="Month">Month</option>
                  <option value="Quarter">Quarter</option>
                  <option value="Week">Week</option>
                </select>
                <input
                  type="date"
                  value={item.periodStart}
                  onChange={(e) => {
                    const updated = [...formData.goalBreakdown];
                    updated[index].periodStart = e.target.value;
                    setFormData((prev) => ({ ...prev, goalBreakdown: updated }));
                  }}
                  className="p-1 border rounded"
                />
                <input
                  type="date"
                  value={item.periodEnd}
                  onChange={(e) => {
                    const updated = [...formData.goalBreakdown];
                    updated[index].periodEnd = e.target.value;
                    setFormData((prev) => ({ ...prev, goalBreakdown: updated }));
                  }}
                  className="p-1 border rounded"
                />
                <input
                  type="number"
                  placeholder="Target"
                  value={item.targetValue}
                  onChange={(e) => {
                    const updated = [...formData.goalBreakdown];
                    updated[index].targetValue = e.target.value;
                    setFormData((prev) => ({ ...prev, goalBreakdown: updated }));
                  }}
                  className="p-1 border rounded"
                />
                <input
                  type="number"
                  placeholder="Actual"
                  value={item.actualValue}
                  onChange={(e) => {
                    const updated = [...formData.goalBreakdown];
                    updated[index].actualValue = e.target.value;
                    setFormData((prev) => ({ ...prev, goalBreakdown: updated }));
                  }}
                  className="p-1 border rounded"
                />
              </div>

              <textarea
                placeholder="Breakdown Notes"
                value={item.notes}
                onChange={(e) => {
                  const updated = [...formData.goalBreakdown];
                  updated[index].notes = e.target.value;
                  setFormData((prev) => ({ ...prev, goalBreakdown: updated }));
                }}
                className="mt-2 w-full p-1 border rounded"
              />
            </div>
          ))}
        </div>

        {/* Goal Notes Section */}
        <div className="border rounded p-2 m-2 bg-gray-50">
          <div className="flex justify-between items-center mb-2">
            <p className="font-bold text-sm">Goal Notes</p>
            <button
              type="button"
              className="bg-lime-600 hover:bg-lime-700 text-white text-xs font-bold m-1 p-1 rounded"
              onClick={() =>
                setFormData((prev) => ({
                  ...prev,
                  goalNotes: [
                    ...prev.goalNotes,
                    {
                      noteText: '',
                      noteDate: new Date().toISOString(),
                      sharedWithAll: false,
                    },
                  ],
                }))
              }
            >
              <PlusIcon className="h-4 w-4 inline" />
            </button>
          </div>

          {formData.goalNotes.map((note, index) => (
            <div key={index} className="p-2 border rounded mb-2 bg-white">
              <div className="flex justify-between items-center mb-2">
                <p className="font-bold text-xs">Note {index + 1}</p>
                <button
                  type="button"
                  className="bg-red-500 hover:bg-red-600 text-white text-xs font-bold p-1 rounded"
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      goalNotes: prev.goalNotes.filter(
                        (_, idx) => idx !== index
                      ),
                    }))
                  }
                >
                  <MinusIcon className="h-4 w-4 inline" />
                </button>
              </div>

              <textarea
                placeholder="Note Text"
                value={note.noteText}
                onChange={(e) => {
                  const updated = [...formData.goalNotes];
                  updated[index].noteText = e.target.value;
                  setFormData((prev) => ({ ...prev, goalNotes: updated }));
                }}
                className="w-full p-1 border rounded"
              />
              <div className="mt-1">
                <label className="mr-2 text-xs">
                  <input
                    type="checkbox"
                    checked={note.sharedWithAll}
                    onChange={(e) => {
                      const updated = [...formData.goalNotes];
                      updated[index].sharedWithAll = e.target.checked;
                      setFormData((prev) => ({ ...prev, goalNotes: updated }));
                    }}
                  />
                  <span className="ml-1">Share with all</span>
                </label>
              </div>
            </div>
          ))}
        </div>

        {/* Action Buttons */}
        <div className="bg-white m-1 px-1 py-3 rounded">
          <EtBtnFormBasic name="Create" type="create" handleClick={handleSubmit} />
          <EtBtnFormBasic name="Clear" type="clear" handleClick={handleClear} />
          <EtBtnFormBasic name="Cancel" type="cancel" handleClick={handleCancel} />
        </div>
      </form>
    </div>
  );
};

export default EtCreateUserMissionGoalComp;